import Link from 'next/link';
import ShopifySEO from '../../components/shopify/ShopifySEO';
import ShopifyTool from '../../components/shopify/ShopifyTool';
import { shopifyPages } from '../../content/shopify';
import {
  getGuideUi,
  guideHubPath,
  guidePath
} from '../../content/shopify/guideLocale';
import { standalonePage } from '../../content/shopify/standalone';
import { loadMessages } from '../../utils/i18n';

const locales = ['en', 'pl', 'uk'];

export default function ShopifyGuide({ page, related, locale }) {
  const ui = getGuideUi(locale);
  const alternatePaths = Object.fromEntries(
    locales.map((item) => [item, guidePath(page.slug, item)])
  );
  return (
    <div lang={locale} className="bg-white text-slate-900">
      <ShopifySEO
        title={page.title}
        description={page.description}
        path={guidePath(page.slug, locale)}
        article
        locale={locale}
        alternatePaths={alternatePaths}
        breadcrumb={{
          home: ui.home,
          hub: ui.hub,
          hubPath: guideHubPath(locale)
        }}
      />
      <header className="border-b border-slate-200 bg-slate-50 px-6 pb-14 pt-12">
        <div className="mx-auto max-w-6xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-8 flex flex-wrap gap-2 text-sm text-slate-600"
          >
            <Link href="/" locale={locale} className="underline">
              {ui.home}
            </Link>
            <span aria-hidden="true">/</span>
            <Link
              href={guideHubPath(locale)}
              locale={false}
              className="underline"
            >
              {ui.hub}
            </Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">{page.heading}</span>
          </nav>
          <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-blue-700">
            Shopify / {page.category}
          </p>
          <h1 className="max-w-4xl text-4xl font-bold leading-tight md:text-5xl">
            {page.heading}
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600">
            {page.intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href="#tool"
              className="rounded-xl bg-blue-700 px-6 py-3 font-semibold text-white hover:bg-blue-800"
            >
              {page.tool.title}
            </a>
            <a
              href="https://apps.shopify.com/afivio"
              className="rounded-xl border border-slate-300 bg-white px-6 py-3 font-semibold hover:border-blue-600"
            >
              {ui.app}
            </a>
          </div>
          <p className="mt-6 text-sm text-slate-500">{ui.byline}</p>
        </div>
      </header>
      <div className="mx-auto grid max-w-6xl gap-12 px-6 py-12 lg:grid-cols-[minmax(0,1fr)_250px]">
        <article className="min-w-0">
          <section
            id="tool"
            aria-label={page.tool.title}
            className="mb-12 scroll-mt-24"
          >
            <ShopifyTool
              key={`${locale}-${page.slug}`}
              tool={page.tool}
              locale={locale}
            />
          </section>
          {page.sections.map((section, index) => (
            <section
              key={section.heading}
              id={`section-${index}`}
              className="mb-12 scroll-mt-24"
            >
              <h2 className="mb-5 text-2xl font-bold">{section.heading}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph} className="mb-4 leading-8 text-slate-700">
                  {paragraph}
                </p>
              ))}
              {section.bullets && (
                <ul className="list-disc space-y-3 pl-6 leading-7 text-slate-700">
                  {section.bullets.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
          <section
            id="example"
            className="mb-12 rounded-2xl border border-blue-100 bg-blue-50 p-7"
          >
            <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-blue-700">
              {ui.example}
            </p>
            <h2 className="mb-4 text-2xl font-bold">{page.example.title}</h2>
            <p className="whitespace-pre-line leading-8 text-slate-700">
              {page.example.text}
            </p>
          </section>
          <section id="faq" className="mb-12">
            <h2 className="mb-6 text-2xl font-bold">{ui.faq}</h2>
            <div className="divide-y divide-slate-200">
              {page.faqs.map((faq) => (
                <details key={faq.question} className="py-5">
                  <summary className="cursor-pointer font-semibold leading-7">
                    {faq.question}
                  </summary>
                  <p className="mt-4 leading-8 text-slate-700">{faq.answer}</p>
                </details>
              ))}
            </div>
          </section>
          {page.sources?.length > 0 && (
            <section className="mb-12">
              <h2 className="mb-4 text-xl font-bold">{ui.furtherReading}</h2>
              <ul className="space-y-3">
                {page.sources.map((source) => (
                  <li key={source.url}>
                    <a className="text-blue-700 underline" href={source.url}>
                      {source.title}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className="rounded-2xl bg-slate-900 p-8 text-white">
            <h2 className="text-2xl font-bold">{ui.practice}</h2>
            <p className="my-5 leading-7 text-slate-300">{ui.practiceText}</p>
            <div className="flex flex-wrap gap-5">
              <a
                href="https://apps.shopify.com/afivio"
                className="rounded-lg bg-white px-5 py-3 font-semibold text-slate-900"
              >
                {ui.install}
              </a>
              <Link
                locale={locale}
                href="/shopify-integration"
                className="py-3 underline"
              >
                {ui.integration}
              </Link>
            </div>
          </section>
        </article>
        <aside className="min-w-0">
          <nav
            aria-label={ui.onPage}
            className="rounded-2xl border border-slate-200 p-6"
          >
            <p className="mb-4 font-semibold">{ui.onPage}</p>
            <ul className="space-y-4 text-sm leading-6 text-slate-600">
              <li>
                <a href="#tool" className="hover:text-blue-700">
                  {page.tool.title}
                </a>
              </li>
              {page.sections.map((section, index) => (
                <li key={section.heading}>
                  <a href={`#section-${index}`} className="hover:text-blue-700">
                    {section.heading}
                  </a>
                </li>
              ))}
              <li>
                <a href="#example">{ui.example}</a>
              </li>
              <li>
                <a href="#faq">{ui.faq}</a>
              </li>
            </ul>
          </nav>
          <section className="mt-8">
            <h2 className="mb-4 text-lg font-bold">{ui.next}</h2>
            <ul className="space-y-5">
              {related.map((item) => (
                <li key={item.slug}>
                  <Link
                    href={guidePath(item.slug, locale)}
                    locale={false}
                    className="text-blue-700 underline underline-offset-4"
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              href={guideHubPath(locale)}
              locale={false}
              className="mt-6 block font-semibold text-blue-700"
            >
              {ui.browse}
            </Link>
          </section>
        </aside>
      </div>
    </div>
  );
}

export function getStaticPaths() {
  return {
    paths: shopifyPages.flatMap((page) =>
      locales.map((locale) => ({ params: { slug: page.slug }, locale }))
    ),
    fallback: false
  };
}

export async function getStaticProps({ params, locale = 'en' }) {
  const source = shopifyPages.find((item) => item.slug === params.slug);
  if (!source || !locales.includes(locale)) return { notFound: true };
  const page = locale === 'en' ? source : standalonePage(source.slug, locale);
  const related = source.related
    .map((slug) => shopifyPages.find((item) => item.slug === slug))
    .filter(Boolean)
    .map((item) =>
      locale === 'en'
        ? { slug: item.slug, title: item.title }
        : (() => {
            const localized = standalonePage(item.slug, locale);
            return { slug: localized.slug, title: localized.title };
          })()
    );
  return {
    props: {
      page,
      related,
      locale,
      messages: await loadMessages(locale, {
        includeShopifyStandalone: true,
        includeShopifyGuides: true
      })
    }
  };
}
